import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const DishForm = ({ dish, onSave, onCancel, isOpen }) => {
  const [formData, setFormData] = useState({
    name: '',
    cuisine: '',
    prepTime: '',
    servings: '',
    ingredients: [],
    dietaryRestrictions: [],
    instructions: '',
    notes: '',
    image: '',
    imageAlt: ''
  });

  const [newIngredient, setNewIngredient] = useState('');
  const [ingredientSuggestions, setIngredientSuggestions] = useState([]);
  const [errors, setErrors] = useState({});
  const [imageInputType, setImageInputType] = useState('url'); // 'url' or 'upload'
  const [uploadedImageFile, setUploadedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  const cuisineOptions = [
    'Italian', 'French', 'Asian', 'Mexican', 'Indian', 'Mediterranean', 'American', 'Thai', 'Japanese', 'Greek'
  ];

  const dietaryOptions = [
    { id: 'vegetarian', label: 'Vegetarian', icon: 'Leaf' },
    { id: 'vegan', label: 'Vegan', icon: 'Sprout' },
    { id: 'gluten-free', label: 'Gluten-Free', icon: 'Wheat' },
    { id: 'dairy-free', label: 'Dairy-Free', icon: 'Milk' },
    { id: 'nut-free', label: 'Nut-Free', icon: 'Nut' },
    { id: 'halal', label: 'Halal', icon: 'Moon' },
    { id: 'kosher', label: 'Kosher', icon: 'Star' }
  ];

  const commonIngredients = [
    'Chicken breast', 'Ground beef', 'Salmon fillet', 'Olive oil', 'Garlic', 'Onion', 'Tomatoes',
    'Bell peppers', 'Mushrooms', 'Spinach', 'Basil', 'Oregano', 'Salt', 'Black pepper',
    'Pasta', 'Rice', 'Potatoes', 'Flour', 'Eggs', 'Milk', 'Cheese', 'Butter'
  ];

  useEffect(() => {
    if (dish) {
      setFormData({
        name: dish?.name || '',
        cuisine: dish?.cuisine || '',
        prepTime: dish?.prepTime?.toString() || '',
        servings: dish?.servings?.toString() || '',
        ingredients: dish?.ingredients || [],
        dietaryRestrictions: dish?.dietaryRestrictions || [],
        instructions: dish?.instructions || '',
        notes: dish?.notes || '',
        image: dish?.image || '',
        imageAlt: dish?.imageAlt || ''
      });
      // Set image preview if there's an existing image
      if (dish?.image) {
        setImagePreview(dish?.image);
        setImageInputType('url');
      }
    } else {
      setFormData({
        name: '',
        cuisine: '',
        prepTime: '',
        servings: '',
        ingredients: [],
        dietaryRestrictions: [],
        instructions: '',
        notes: '',
        image: '',
        imageAlt: ''
      });
      setImagePreview('');
      setUploadedImageFile(null);
      setImageInputType('url');
    }
    setErrors({});
  }, [dish, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Update image preview when URL changes
    if (field === 'image' && imageInputType === 'url') {
      setImagePreview(value);
    }
  };

  const handleImageUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file && file?.type?.startsWith('image/')) {
      setUploadedImageFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e?.target?.result;
        setImagePreview(dataUrl);
        setFormData(prev => ({ ...prev, image: dataUrl }));
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleImageInputTypeChange = (type) => {
    setImageInputType(type);
    if (type === 'url') {
      setUploadedImageFile(null);
      setImagePreview(formData?.image || '');
    } else {
      setFormData(prev => ({ ...prev, image: '' }));
      setImagePreview('');
    }
  };

  const removeImage = () => {
    setFormData(prev => ({ ...prev, image: '', imageAlt: '' }));
    setUploadedImageFile(null);
    setImagePreview('');
    if (imageInputType === 'upload') {
      // Clear file input
      const fileInput = document.getElementById('image-upload-input');
      if (fileInput) {
        fileInput.value = '';
      }
    }
  };

  const handleIngredientInput = (value) => {
    setNewIngredient(value);
    if (value?.length > 1) {
      const suggestions = commonIngredients?.filter(ingredient =>
        ingredient?.toLowerCase()?.includes(value?.toLowerCase()) &&
        !formData?.ingredients?.includes(ingredient)
      );
      setIngredientSuggestions(suggestions?.slice(0, 5));
    } else {
      setIngredientSuggestions([]);
    }
  };

  const addIngredient = (ingredient) => {
    if (ingredient && !formData?.ingredients?.includes(ingredient)) {
      setFormData(prev => ({
        ...prev,
        ingredients: [...prev?.ingredients, ingredient]
      }));
      setNewIngredient('');
      setIngredientSuggestions([]);
    }
  };

  const removeIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev?.ingredients?.filter((_, i) => i !== index)
    }));
  };

  const handleDietaryToggle = (restriction) => {
    setFormData(prev => ({
      ...prev,
      dietaryRestrictions: prev?.dietaryRestrictions?.includes(restriction)
        ? prev?.dietaryRestrictions?.filter(r => r !== restriction)
        : [...prev?.dietaryRestrictions, restriction]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Dish name is required';
    }

    if (!formData?.cuisine) {
      newErrors.cuisine = 'Cuisine type is required';
    }

    if (!formData?.prepTime || formData?.prepTime < 1) {
      newErrors.prepTime = 'Preparation time must be at least 1 minute';
    }

    if (!formData?.servings || formData?.servings < 1) {
      newErrors.servings = 'Servings must be at least 1';
    }

    if (formData?.ingredients?.length === 0) {
      newErrors.ingredients = 'At least one ingredient is required';
    }

    if (!formData?.instructions?.trim()) {
      newErrors.instructions = 'Cooking instructions are required';
    }

    // Validate image alt text if image is provided
    if (formData?.image && !formData?.imageAlt?.trim()) {
      newErrors.imageAlt = 'Image description is required when an image is provided';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      const dishData = {
        ...formData,
        prepTime: parseInt(formData?.prepTime),
        servings: parseInt(formData?.servings),
        id: dish?.id || Date.now(),
        createdAt: dish?.createdAt || new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString(),
        usageCount: dish?.usageCount || 0,
        rating: dish?.rating || null,
        lastUsed: dish?.lastUsed || null
      };
      onSave(dishData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-background/80 backdrop-blur-sm" onClick={onCancel} />

        <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-card shadow-warm-lg rounded-lg">
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">
              {dish ? 'Edit Dish' : 'Add New Dish'}
            </h2>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Dish Name"
                type="text"
                placeholder="Enter dish name"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Cuisine Type <span className="text-error">*</span>
                </label>
                <select
                  value={formData?.cuisine}
                  onChange={(e) => handleInputChange('cuisine', e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  required
                >
                  <option value="">Select cuisine</option>
                  {cuisineOptions?.map(cuisine => (
                    <option key={cuisine} value={cuisine}>{cuisine}</option>
                  ))}
                </select>
                {errors?.cuisine && (
                  <p className="mt-1 text-sm text-error">{errors?.cuisine}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Preparation Time (minutes)"
                type="number"
                placeholder="30"
                value={formData?.prepTime}
                onChange={(e) => handleInputChange('prepTime', e?.target?.value)}
                error={errors?.prepTime}
                min="1"
                required
              />

              <Input
                label="Servings"
                type="number"
                placeholder="4"
                value={formData?.servings}
                onChange={(e) => handleInputChange('servings', e?.target?.value)}
                error={errors?.servings}
                min="1"
                required
              />
            </div>

            {/* Image Section */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Dish Image
              </label>
              
              {/* Image Input Type Toggle */}
              <div className="flex space-x-1 mb-4 bg-muted rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => handleImageInputTypeChange('url')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-warm ${
                    imageInputType === 'url' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Link" size={16} className="mr-2 inline" />
                  Image URL
                </button>
                <button
                  type="button"
                  onClick={() => handleImageInputTypeChange('upload')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-warm ${
                    imageInputType === 'upload' ?'bg-background text-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon name="Upload" size={16} className="mr-2 inline" />
                  Upload Image
                </button>
              </div>

              {/* Image Input Fields */}
              {imageInputType === 'url' ? (
                <Input
                  label="Image URL"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={formData?.image}
                  onChange={(e) => handleInputChange('image', e?.target?.value)}
                />
              ) : (
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Upload Image File
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-1">
                      <input
                        id="image-upload-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 transition-warm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Image Preview */}
              {imagePreview && (
                <div className="mt-4">
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-32 h-32 object-cover rounded-lg border border-border"
                      onError={() => {
                        setImagePreview('');
                        if (imageInputType === 'url') {
                          setFormData(prev => ({ ...prev, image: '' }));
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-error text-error-foreground rounded-full flex items-center justify-center hover:bg-error/90 transition-warm"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                </div>
              )}

              {/* Image Alt Text */}
              <div className="mt-4">
                <Input
                  label="Image Description (Alt Text)"
                  type="text"
                  placeholder="Describe the image for accessibility"
                  value={formData?.imageAlt}
                  onChange={(e) => handleInputChange('imageAlt', e?.target?.value)}
                  error={errors?.imageAlt}
                  helperText="Required when image is provided. Describe what the image shows for visually impaired users."
                />
              </div>
            </div>

            {/* Ingredients */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Ingredients <span className="text-error">*</span>
              </label>
              <div className="relative">
                <div className="flex space-x-2">
                  <Input
                    type="text"
                    placeholder="Add ingredient..."
                    value={newIngredient}
                    onChange={(e) => handleIngredientInput(e?.target?.value)}
                    onKeyPress={(e) => {
                      if (e?.key === 'Enter') {
                        e?.preventDefault();
                        addIngredient(newIngredient);
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addIngredient(newIngredient)}
                  >
                    <Icon name="Plus" size={16} />
                  </Button>
                </div>

                {/* Suggestions */}
                {ingredientSuggestions?.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-md shadow-warm-lg">
                    {ingredientSuggestions?.map((suggestion, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => addIngredient(suggestion)}
                        className="w-full px-3 py-2 text-left hover:bg-muted transition-warm"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Ingredient List */}
              <div className="flex flex-wrap gap-2 mt-3">
                {formData?.ingredients?.map((ingredient, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 bg-muted text-muted-foreground px-3 py-1 rounded-full"
                  >
                    <span className="text-sm">{ingredient}</span>
                    <button
                      type="button"
                      onClick={() => removeIngredient(index)}
                      className="text-error hover:text-error/80"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
              {errors?.ingredients && (
                <p className="mt-1 text-sm text-error">{errors?.ingredients}</p>
              )}
            </div>

            {/* Dietary Restrictions */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Dietary Restrictions
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {dietaryOptions?.map((option) => (
                  <label key={option?.id} className="flex items-center space-x-2 cursor-pointer">
                    <Checkbox
                      checked={formData?.dietaryRestrictions?.includes(option?.id)}
                      onChange={() => handleDietaryToggle(option?.id)}
                    />
                    <Icon name={option?.icon} size={16} className="text-primary" />
                    <span className="text-sm text-foreground">{option?.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Cooking Instructions <span className="text-error">*</span>
              </label>
              <textarea
                value={formData?.instructions}
                onChange={(e) => handleInputChange('instructions', e?.target?.value)}
                placeholder="Enter step-by-step cooking instructions..."
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-vertical"
                required
              />
              {errors?.instructions && (
                <p className="mt-1 text-sm text-error">{errors?.instructions}</p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Additional Notes
              </label>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleInputChange('notes', e?.target?.value)}
                placeholder="Any additional notes, tips, or variations..."
                rows={3}
                className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-vertical"
              />
            </div>
          </form>

          <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {dish ? 'Update Dish' : 'Add Dish'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DishForm;